import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { SECTIONS, type SectionKey } from './sections';

export interface Crumb {
  readonly label: string;
  readonly to?: string;
}

export interface PageHeaderProps {
  readonly section: SectionKey;
  readonly icon?: ComponentType<{ className?: string; strokeWidth?: number | string }>;
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly breadcrumb?: readonly Crumb[];
  readonly actions?: ReactNode;
}

export function PageHeader({
  section,
  icon,
  title,
  subtitle,
  breadcrumb,
  actions,
}: PageHeaderProps) {
  const sec = SECTIONS[section];
  const Icon = icon ?? sec.icon;
  const mobileBack = [...(breadcrumb ?? [])].reverse().find((item) => item.to);

  return (
    <div className="native-screen-enter mx-auto max-w-7xl px-4 pt-3 sm:px-6 md:pt-4">
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="mb-2 hidden items-center gap-2 text-caption text-ink-muted md:flex">
          {breadcrumb.map((b, i) => (
            <span key={`${b.label}-${i}`} className="inline-flex items-center gap-2">
              {b.to ? (
                <Link to={b.to} className="hover:text-ink hover:underline">
                  {b.label}
                </Link>
              ) : (
                <span>{b.label}</span>
              )}
              {i < breadcrumb.length - 1 && <span className="text-ink-muted/60">/</span>}
            </span>
          ))}
        </nav>
      )}

      <div
        className="overflow-hidden rounded-[30px] border border-border shadow-card backdrop-blur-xl"
        style={{
          background: `linear-gradient(160deg, rgba(255,255,255,0.28) 0%, ${sec.plate} 100%)`,
          borderColor: sec.edge,
        }}
      >
        <div className="px-4 py-4 md:px-5 md:py-5">
          {mobileBack && (
            <div className="mb-3">
              <Link
                to={mobileBack.to!}
                className="app-chip inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-small font-semibold text-ink-muted shadow-sm backdrop-blur transition-colors hover:bg-surface-raised hover:text-ink"
              >
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
                <span>Back to {mobileBack.label}</span>
              </Link>
            </div>
          )}
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
              <div className="app-chip inline-flex items-center gap-2 rounded-full border border-border px-2.5 py-1 text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted ring-1 ring-white/30">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-full text-white shadow-sm"
                  style={{ background: sec.color }}
                  aria-hidden
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                </span>
                <span>{sec.label}</span>
              </div>
              <h1 className="mt-3.5 text-[2.32rem] font-semibold tracking-[-0.04em] text-ink md:text-[2.55rem]">
                {title}
              </h1>
              {subtitle && (
                <div className="mt-2 max-w-2xl text-body leading-7 text-ink-muted md:text-body">
                  {subtitle}
                </div>
              )}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2 md:justify-end">{actions}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
